import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";
import { COLORS, FONTS, SPRING } from "./constants";

// ─── Nodes: bigger, bolder, more of them ────────────────────────

interface GNode {
  id: string;
  label: string;
  type: string;
  x: number;
  y: number;
  color: string;
  delay: number;
  size: number;
}

const NODES: GNode[] = [
  { id: "sarah", label: "Sarah Chen", type: "PERSON", x: 380, y: 420, color: COLORS.accentPink, delay: 15, size: 48 },
  { id: "acme", label: "Acme Corp", type: "ORG", x: 760, y: 340, color: COLORS.accentOrange, delay: 25, size: 50 },
  { id: "q2", label: "Q2 Proposal", type: "CONCEPT", x: 1200, y: 380, color: COLORS.accentPurple, delay: 35, size: 48 },
  { id: "refund", label: "Refund Policy", type: "CONCEPT", x: 520, y: 640, color: COLORS.accentTeal, delay: 45, size: 48 },
  { id: "board", label: "Board Meeting", type: "EVENT", x: 1000, y: 660, color: COLORS.accentBlue, delay: 55, size: 48 },
  { id: "ops", label: "Operations Team", type: "ORG", x: 220, y: 620, color: COLORS.accentGreen, delay: 60, size: 44 },
  { id: "david", label: "David Park", type: "PERSON", x: 1420, y: 560, color: COLORS.accentPink, delay: 70, size: 44 },
  { id: "contract", label: "Vendor Contract", type: "CONCEPT", x: 900, y: 500, color: COLORS.accentYellow, delay: 50, size: 44 },
  { id: "slack", label: "#general", type: "CHANNEL", x: 1460, y: 320, color: "#E01E5A", delay: 80, size: 40 },
  { id: "pricing", label: "Pricing Strategy", type: "CONCEPT", x: 440, y: 260, color: COLORS.accentPurple, delay: 75, size: 40 },
];

interface GEdge {
  from: string;
  to: string;
  label: string;
  delay: number;
  color: string;
}

const EDGES: GEdge[] = [
  { from: "sarah", to: "refund", label: "APPROVED", delay: 55, color: COLORS.accentPink },
  { from: "sarah", to: "ops", label: "LEADS", delay: 65, color: COLORS.accentGreen },
  { from: "acme", to: "q2", label: "DISCUSSED IN", delay: 50, color: COLORS.accentOrange },
  { from: "q2", to: "board", label: "PRESENTED AT", delay: 70, color: COLORS.accentPurple },
  { from: "refund", to: "acme", label: "UPDATED FOR", delay: 75, color: COLORS.accentTeal },
  { from: "david", to: "board", label: "ATTENDING", delay: 85, color: COLORS.accentBlue },
  { from: "contract", to: "acme", label: "SIGNED BY", delay: 80, color: COLORS.accentYellow },
  { from: "sarah", to: "contract", label: "REVIEWED", delay: 90, color: COLORS.accentPink },
  { from: "acme", to: "slack", label: "MENTIONED IN", delay: 95, color: "#E01E5A" },
  { from: "pricing", to: "sarah", label: "OWNED BY", delay: 100, color: COLORS.accentPurple },
  { from: "pricing", to: "acme", label: "TARGETS", delay: 105, color: COLORS.accentOrange },
  { from: "david", to: "q2", label: "CO-AUTHORED", delay: 110, color: COLORS.accentPurple },
];

const nodeMap = new Map(NODES.map((n) => [n.id, n]));

// ─── Ambient floating particles ────────────────────────────────

const AmbientParticles: React.FC = () => {
  const frame = useCurrentFrame();
  const particles = Array.from({ length: 30 }, (_, i) => {
    const seed = i * 137.5;
    const x = (seed * 7.3) % 1920;
    const baseY = (seed * 3.7) % 1080;
    const y = baseY + Math.sin((frame + seed) * 0.015) * 30;
    const size = 2 + (i % 3);
    const opacity = 0.08 + Math.sin((frame + seed) * 0.02) * 0.06;
    const colors = [COLORS.accentBlue, COLORS.accentPurple, COLORS.accentTeal, COLORS.accentPink];
    const color = colors[i % colors.length];

    return (
      <div
        key={i}
        style={{
          position: "absolute",
          left: x,
          top: y,
          width: size,
          height: size,
          borderRadius: "50%",
          background: color,
          opacity,
          boxShadow: `0 0 ${size * 3}px ${color}`,
        }}
      />
    );
  });
  return <>{particles}</>;
};

// ─── Flowing edge particles ────────────────────────────────────

const EdgeParticles: React.FC<{
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  color: string;
  delay: number;
}> = ({ fromX, fromY, toX, toY, color, delay }) => {
  const frame = useCurrentFrame();
  if (frame < delay + 20) return null;

  return (
    <>
      {[0, 1, 2].map((i) => {
        const period = 50;
        const localFrame = (frame - delay - 20 + i * 17) % period;
        const t = localFrame / period;
        const x = fromX + (toX - fromX) * t;
        const y = fromY + (toY - fromY) * t;
        const opacity = interpolate(t, [0, 0.1, 0.8, 1], [0, 0.9, 0.9, 0]);
        const size = interpolate(t, [0, 0.5, 1], [3, 6, 3]);

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x - size / 2,
              top: y - size / 2,
              width: size,
              height: size,
              borderRadius: "50%",
              background: color,
              opacity,
              boxShadow: `0 0 ${size * 2}px ${color}, 0 0 ${size * 4}px ${color}50`,
            }}
          />
        );
      })}
    </>
  );
};

// ─── Main Scene ────────────────────────────────────────────────

export const Scene08Graph: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [700, 750], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const visibleEdges = EDGES.filter((e) => frame > e.delay + 10).length;
  const visibleNodes = NODES.filter((n) => frame > n.delay + 5).length;

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.04} />
      <AmbientParticles />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 40,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontSize: 46,
            fontFamily: FONTS.heading,
            fontWeight: 800,
            color: COLORS.textPrimary,
            letterSpacing: -1,
          }}
        >
          See How Your Knowledge{" "}
          <span
            style={{
              background: `linear-gradient(135deg, ${COLORS.accentTeal}, ${COLORS.accentBlue})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Connects
          </span>
        </div>
        <div
          style={{
            fontSize: 20,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 8,
          }}
        >
          The Knowledge Graph maps people, projects, and ideas across your organization
        </div>
      </div>

      {/* ─── EDGES ─── */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        <defs>
          <filter id="edgeGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {EDGES.map((edge, i) => {
          const fromNode = nodeMap.get(edge.from);
          const toNode = nodeMap.get(edge.to);
          if (!fromNode || !toNode) return null;

          const edgeAppear = spring({
            frame: frame - edge.delay,
            fps,
            config: SPRING.gentle,
          });

          const progress = interpolate(edgeAppear, [0, 1], [0, 1]);
          const opacity = interpolate(edgeAppear, [0, 0.2], [0, 1], {
            extrapolateRight: "clamp",
          });

          const endX = fromNode.x + (toNode.x - fromNode.x) * progress;
          const endY = fromNode.y + (toNode.y - fromNode.y) * progress;

          const midX = (fromNode.x + toNode.x) / 2;
          const midY = (fromNode.y + toNode.y) / 2;

          return (
            <React.Fragment key={i}>
              {/* Glow line */}
              <line
                x1={fromNode.x}
                y1={fromNode.y}
                x2={endX}
                y2={endY}
                stroke={edge.color}
                strokeWidth={4}
                opacity={opacity * 0.2}
                filter="url(#edgeGlow)"
              />

              {/* Main line */}
              <line
                x1={fromNode.x}
                y1={fromNode.y}
                x2={endX}
                y2={endY}
                stroke={edge.color}
                strokeWidth={2.5}
                opacity={opacity * 0.8}
                strokeLinecap="round"
              />

              {/* Edge label pill */}
              {progress > 0.7 && (
                <g opacity={interpolate(progress, [0.7, 1], [0, 1])}>
                  <rect
                    x={midX - edge.label.length * 4.5 - 8}
                    y={midY - 14}
                    width={edge.label.length * 9 + 16}
                    height={22}
                    rx={11}
                    fill={COLORS.bg}
                    fillOpacity={0.85}
                    stroke={edge.color}
                    strokeWidth={1}
                    strokeOpacity={0.4}
                  />
                  <text
                    x={midX}
                    y={midY}
                    textAnchor="middle"
                    fill={edge.color}
                    fontSize={12}
                    fontFamily={FONTS.mono}
                    fontWeight={600}
                    letterSpacing={0.5}
                  >
                    {edge.label}
                  </text>
                </g>
              )}
            </React.Fragment>
          );
        })}
      </svg>

      {/* ─── EDGE PARTICLES ─── */}
      {EDGES.map((edge, i) => {
        const fromNode = nodeMap.get(edge.from);
        const toNode = nodeMap.get(edge.to);
        if (!fromNode || !toNode) return null;

        return (
          <EdgeParticles
            key={`p-${i}`}
            fromX={fromNode.x}
            fromY={fromNode.y}
            toX={toNode.x}
            toY={toNode.y}
            color={edge.color}
            delay={edge.delay}
          />
        );
      })}

      {/* ─── NODES ─── */}
      {NODES.map((node, i) => {
        const nodeAppear = spring({
          frame: frame - node.delay,
          fps,
          config: SPRING.bouncy,
        });

        const scale = interpolate(nodeAppear, [0, 1], [0, 1]);
        const opacity = interpolate(nodeAppear, [0, 1], [0, 1]);

        const floatX = Math.cos((frame + i * 25) * 0.02) * 3;
        const floatY = Math.sin((frame + i * 20) * 0.025) * 4;

        const glowSize = interpolate(
          Math.sin((frame + i * 10) * 0.04),
          [-1, 1],
          [node.size * 0.4, node.size * 0.7],
        );

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: node.x + floatX,
              top: node.y + floatY,
              transform: `translate(-50%, -50%) scale(${scale})`,
              opacity,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              zIndex: 5,
            }}
          >
            {/* Outer glow */}
            <div
              style={{
                position: "absolute",
                width: node.size * 2 + 20,
                height: node.size * 2 + 20,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${node.color}15 0%, transparent 70%)`,
                top: -(node.size + 10) + node.size,
                left: -(node.size + 10) + node.size,
              }}
            />

            {/* Node circle */}
            <div
              style={{
                width: node.size * 2,
                height: node.size * 2,
                borderRadius: "50%",
                background: `linear-gradient(145deg, ${node.color}18, ${node.color}08)`,
                border: `1.5px solid ${node.color}70`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 ${glowSize * 0.6}px ${node.color}30`,
                backdropFilter: "blur(4px)",
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 9,
                  color: `${node.color}CC`,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                }}
              >
                {node.type}
              </span>
            </div>

            {/* Node label */}
            <span
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                color: COLORS.textPrimary,
                fontWeight: 500,
                textShadow: `0 1px 6px ${COLORS.bg}`,
                letterSpacing: 0.2,
              }}
            >
              {node.label}
            </span>
          </div>
        );
      })}

      {/* ─── BOTTOM STATS BAR ─── */}
      {(() => {
        const statsAppear = spring({ frame: frame - 120, fps, config: SPRING.snappy });
        return (
          <div
            style={{
              position: "absolute",
              bottom: 100,
              left: "50%",
              transform: "translate(-50%, 0)",
              display: "flex",
              gap: 60,
              opacity: interpolate(statsAppear, [0, 1], [0, 1]),
              padding: "16px 40px",
              borderRadius: 14,
              background: `${COLORS.bgCard}90`,
              border: `1px solid ${COLORS.borderLight}`,
            }}
          >
            {[
              { value: String(visibleNodes), label: "Entities", color: COLORS.accentTeal },
              { value: String(visibleEdges), label: "Relationships", color: COLORS.accentPurple },
              { value: "6", label: "Entity Types", color: COLORS.accentBlue },
              { value: "Real-time", label: "Sync", color: COLORS.accentOrange },
            ].map((stat, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 28, fontWeight: 800, color: stat.color }}>
                  {stat.value}
                </div>
                <div style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.textMuted, marginTop: 2 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ─── LEGEND (top-right) ─── */}
      {(() => {
        const legendAppear = spring({ frame: frame - 100, fps, config: SPRING.snappy });
        const types = [
          { type: "PERSON", color: COLORS.accentPink },
          { type: "ORG", color: COLORS.accentOrange },
          { type: "CONCEPT", color: COLORS.accentPurple },
          { type: "EVENT", color: COLORS.accentBlue },
          { type: "CHANNEL", color: "#E01E5A" },
        ];
        return (
          <div
            style={{
              position: "absolute",
              top: 120,
              right: 80,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              opacity: interpolate(legendAppear, [0, 1], [0, 1]),
              padding: "14px 18px",
              borderRadius: 12,
              background: `${COLORS.bgCard}80`,
              border: `1px solid ${COLORS.border}`,
            }}
          >
            {types.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: t.color,
                    boxShadow: `0 0 8px ${t.color}60`,
                  }}
                />
                <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: COLORS.textSecondary }}>
                  {t.type}
                </span>
              </div>
            ))}
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
